
/**
 * 🦖 古生物化石鉴定增强模块 (Fossil Structural Logic Matrix)
 * 目标：提供深度的化石解构分类标准树，强制排除常见混淆对。
 */
export const FOSSIL_LOGIC = {
  fossilRules: `
  === 🦖 古生物化石鉴定专家逻辑树 (v8.1.0 破壳协议) ===
  **核心指令**: 一旦在样本中检测到任何“疑似生物印痕、螺旋、对称壳体或植物脉络”，强制放弃普通岩石鉴定逻辑，全面启动化石结构解剖树。如果看到“骨骼”，不要轻易定为恐龙，必须走以下结构排雷程序。

  [0. 三叶虫演化形态解剖树 (Trilobite Evolution Matrix)]
  - **群体死亡/蜕壳岩板特征**: 在寒武纪页岩中，经常能看到同一岩板上密集出现多个三叶虫印痕，由于蜕壳行为或风暴沉积灾变导致。
  - **基本鉴定红线**: 表面必须看到明显的纵向“轴叶”(中轴) 和两侧“肋叶”的【三分性】，这是定性为三叶虫的底线。
  - **寒武纪早中期型 (The Cambrian Classics)**:
    - **莱德利基虫家族 (Redlichia & Eoredlichia)**: 莱德利基虫的原始/过渡形态，具有明显向后延伸的细长颊刺，胸节各肋刺末端游离且清晰外伸。真莱德利基虫特征为半圆形极其宽大的头部面线，夸张长颊刺，尾甲退化。
    - **武定虫 (Wudingaspis)**: 头甲呈典型的宽半圆形，不具备极其夸张延伸的长颊刺，体节数量较多分界分明，尾甲极其微小。
    - **云南头虫 (Yunnanocephalus)**: 体型偏小巧紧凑，头甲极度饱满圆润，中轴部位常有多条明显的横向深沟。
    - **女神双耳虫 (Amphoton deois)**: 具有略微加长/矩形化的头鞍，整体身形修长，胸节分界明显。由于特殊埋葬，常会在标本上呈现出“双眼/双耳”位置隆起的微小视觉错觉。
    - **谢高德虫 (Shergoldia)**: 头甲和尾甲极其宽大、延展，且表面极其“平缓光滑”。
  - **奥陶纪-志留纪重装甲防卫型 (Ordovician & Silurian Forms)**: 
    - **大尾重甲平滑型 (如光大壳虫，假帝王虫)**: 头尾等大，缺乏棘刺。
    - **重结节与紧凑防御型 (如高罗王冠虫)**: 头部布满了极其显著密集的颗粒状瘤/结节。双眼通常凸出。尾甲极大。
    - **卷曲防卫型**: 卷曲呈球形。

  [1. 珊瑚与无脊椎底栖生物解剖树 A vs B (Benthic Matrix)]
  - **单体四射珊瑚 (Rugose Coral) vs 动物牙齿/角**：
    - 四射珊瑚通常外形如“牛角”，且必须在截面寻找【辐射状的隔壁 (Septa)】特征。
    - 动物牙齿截面为同心圆层状（牙釉质-牙本质）且表面有釉质光泽。
  - **床板珊瑚/复体四射珊瑚 vs 常见多孔构造岩石 / 现代蜂窝**：
    - 必须寻找像蜂窝一样的密集排列管子。若是床板珊瑚，会在侧面（纵切面）看到水平的【床板 (Tabulae)】横穿管子。
  - **腕足动物 (Brachiopods) vs 双壳/蚌类 (Bivalves) (强制对称性校验)**：
    - 若看到像“贝壳”的化石，绝对禁止随机输出。
    - 鉴定红线：腕足类的对称面是【穿过单个壳的最高点垂直切开左右对称】（两瓣壳不一样大）。双壳类的对称面是【两瓣壳相互对称】（左壳和右壳像镜子一样）。

  [2. 头足类演化解剖树 (Cephalopod Evolution Matrix)]
  - **鹦鹉螺 (Nautiloid) vs 菊石 (Ammonite) (缝合线定生死)**：
    - 看到盘旋状带格子的化石，不要直接叫菊石。
    - 寻找两格之间的交界线（缝合线）。若是【平直或简单微波浪状】，判定为鹦鹉螺类（常出现在古生代早期）；若是【极其复杂的树枝状、菊花叶子状褶皱】，强制判定为菊石（中生代标志）。

  [3. 假化石警报 (Pseudofossil Trap Protocol)]
  - **硅化木 (Petrified Wood) vs 柱状矿物集合体（如电气石/角闪石）**：
    - 硅化木必须有同心圆状的【年轮】和沿着纵切面的【管胞/树丝木纹】结构，且往往保留树皮特征。
  - **锰结核/软锰矿树枝石 (Dendrite) vs 蕨类植物化石 (Fern)**：
    - 黑色像树叶/蕨类的图案如果像水墨画一样平贴在岩石缝隙上，且没有碳质残留或叶脉突起，强制判定为【软锰矿结晶（假化石）】，绝对不是植物化石！

  [4. 遗迹化石与生物扰动 (Trace Fossils & Bioturbation)]
  - 若表面只有不规则的管状突起、洞穴泥痕，可能为虫孔或遗迹化石。描述重心应偏向当时的沉积环境（如浅海潮间带）。

  [5. 地质年代与环境锁 (Deep-Time Anchors)]
  - 输出规范：必须在报告中基于你认出的化石框定【地质年代】（如：三叶虫锁定古生代，菊石锁定中生代）。并在地质背景中描绘它是死于浅海、深海还是沼泽森林。
  `
};
